import React, { useEffect, useMemo, useState } from "react";
import {
  createAdminCourse,
  deleteAdminCourse,
  getAdminCourses,
  updateAdminCourse,
} from "../../../api/coursesAdmin";
import { useNotification } from "../../../context/NotificationContext";
import { useTranslation } from "react-i18next";

const EMPTY_FORM = {
  courseKey: "",
  language: "en",
  title: "",
  description: "",
  gainHeading: "What you will gain:",
  isActive: true,
  gainItems: [{ text: "" }],
};

const mapCourseToForm = (course) => ({
  courseKey: course?.courseKey || "",
  language: String(course?.language || "en").toLowerCase().split("-")[0],
  title: course?.title || "",
  description: course?.description || "",
  gainHeading: course?.gainHeading || "What you will gain:",
  isActive: typeof course?.isActive === "boolean" ? course.isActive : true,
  gainItems: Array.isArray(course?.gainItems)
    ? course.gainItems.map((x) => ({ text: x?.text || "" }))
    : [{ text: "" }],
});

const toPayload = (form) => ({
  courseKey: String(form.courseKey || "").trim(),
  language: String(form.language || "en").toLowerCase(),
  title: String(form.title || "").trim(),
  description: String(form.description || "").trim(),
  gainHeading: String(form.gainHeading || "").trim() || "What you will gain:",
  isActive: Boolean(form.isActive),
  gainItems: (form.gainItems || [])
    .map((x) => ({ text: String(x?.text || "").trim() }))
    .filter((x) => x.text),
});

const CoursesManager = () => {
  const { i18n } = useTranslation();
  const { notify } = useNotification();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await getAdminCourses();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({
        type: "error",
        title: "Failed to load courses",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const sortedItems = useMemo(() => {
    const currentLang = String(i18n.language || "en").toLowerCase().split("-")[0];
    const filtered = items.filter(
      (item) => String(item?.language || "en").toLowerCase().split("-")[0] === currentLang
    );
    return [...filtered].sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0));
  }, [items, i18n.language]);

  const handleCreate = async () => {
    if (!createForm.courseKey.trim() || !createForm.title.trim()) {
      notify({ type: "error", title: "Course key and title are required" });
      return;
    }
    setCreating(true);
    try {
      await createAdminCourse(toPayload(createForm));
      notify({ type: "success", title: "Course created" });
      setCreateForm(EMPTY_FORM);
      await loadItems();
    } catch (error) {
      notify({
        type: "error",
        title: "Create failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm(mapCourseToForm(item));
  };

  const saveEdit = async () => {
    if (!editingId) return;
    if (!editForm.courseKey.trim() || !editForm.title.trim()) {
      notify({ type: "error", title: "Course key and title are required" });
      return;
    }
    try {
      await updateAdminCourse({ id: editingId, payload: toPayload(editForm) });
      notify({ type: "success", title: "Course updated" });
      setEditingId(null);
      setEditForm(EMPTY_FORM);
      await loadItems();
    } catch (error) {
      notify({
        type: "error",
        title: "Update failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete course "${item?.title || item?.courseKey}"?`)) return;
    try {
      await deleteAdminCourse({ id: item.id });
      notify({ type: "success", title: "Course deleted" });
      await loadItems();
    } catch (error) {
      notify({
        type: "error",
        title: "Delete failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  const renderForm = ({ form, setForm }) => (
    <div className="grid sm:grid-cols-2 gap-3">
      <label className="text-sm text-slate-700">
        Course Key
        <input
          type="text"
          value={form.courseKey}
          onChange={(e) => setForm((p) => ({ ...p, courseKey: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <label className="text-sm text-slate-700">
        Language
        <select
          value={form.language}
          onChange={(e) => setForm((p) => ({ ...p, language: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        >
          <option value="en">EN</option>
          <option value="ar">AR</option>
          <option value="ku">KU</option>
        </select>
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">
        Title
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">
        Description
        <textarea
          value={form.description}
          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          className="mt-1 w-full min-h-[90px] rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">
        Gain Heading
        <input
          type="text"
          value={form.gainHeading}
          onChange={(e) => setForm((p) => ({ ...p, gainHeading: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={Boolean(form.isActive)}
          onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
        />
        Active
      </label>

      <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3">
        <p className="text-sm font-semibold text-[#011337] mb-2">Gain Items</p>
        {form.gainItems.map((row, idx) => (
          <div key={idx} className="flex gap-2 mb-2">
            <input
              type="text"
              value={row?.text || ""}
              onChange={(e) =>
                setForm((p) => {
                  const next = [...p.gainItems];
                  next[idx] = { text: e.target.value };
                  return { ...p, gainItems: next };
                })
              }
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={() =>
                setForm((p) => {
                  const next = p.gainItems.filter((_, i) => i !== idx);
                  return { ...p, gainItems: next.length ? next : [{ text: "" }] };
                })
              }
              className="px-3 rounded border border-rose-200 text-rose-700"
            >
              -
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            setForm((p) => ({ ...p, gainItems: [...p.gainItems, { text: "" }] }))
          }
          className="text-xs px-3 py-1.5 rounded border border-slate-300"
        >
          Add gain item
        </button>
      </div>
    </div>
  );

  return (
    <section className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#011337]">Courses Manager</h2>
            <p className="text-sm text-slate-500 mt-1">CRUD courses and manage gain items.</p>
          </div>
          <button
            type="button"
            onClick={loadItems}
            className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm"
          >
            Reload
          </button>
        </div>

        {renderForm({ form: createForm, setForm: setCreateForm })}

        <div className="mt-3 flex justify-end">
          <button
            type="button"
            disabled={creating}
            onClick={handleCreate}
            className="px-4 py-2 rounded-lg bg-[#FE7E3F] text-white hover:bg-[#FE7E3F]/90 text-sm disabled:opacity-60"
          >
            {creating ? "Creating..." : "Create course"}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
        <table className="min-w-full table-fixed">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">ID</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Key</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Lang</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Title</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-sm text-slate-500">
                  Loading courses...
                </td>
              </tr>
            ) : sortedItems.length ? (
              sortedItems.map((item) => (
                <tr key={item.id} className="border-b border-slate-200 align-top">
                  <td className="px-3 py-3 text-sm text-slate-700">{item.id}</td>
                  <td className="px-3 py-3 text-sm text-slate-700">{item.courseKey || "-"}</td>
                  <td className="px-3 py-3 text-sm text-slate-700 uppercase">{item.language || "-"}</td>
                  <td className="px-3 py-3 text-sm font-medium text-slate-900">{item.title || "-"}</td>
                  <td className="px-3 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs ${item.isActive ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>
                      {item.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col gap-2">
                      <button
                        type="button"
                        onClick={() => startEdit(item)}
                        className="text-xs px-2 py-1.5 rounded border border-[#011337] text-[#011337] hover:bg-[#011337]/10"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(item)}
                        className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-sm text-slate-500">
                  No courses found for this language.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editingId ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="text-lg font-semibold text-[#011337]">Edit Course #{editingId}</h3>
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setEditForm(EMPTY_FORM);
              }}
              className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50"
            >
              Close
            </button>
          </div>
          {renderForm({ form: editForm, setForm: setEditForm })}
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={saveEdit}
              className="px-4 py-2 rounded-lg bg-[#011337] text-white hover:bg-[#011337]/90 text-sm"
            >
              Save changes
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default CoursesManager;
